import React, { useEffect, useState } from 'react';
import { TripFilters as TripFiltersType } from '../../types/trip';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { driversApi } from '../../api/drivers.api';
import { User } from '../../types/user';

interface TripFiltersProps {
  filters: TripFiltersType;
  onChange: (filters: TripFiltersType) => void;
}

export const TripFilters: React.FC<TripFiltersProps> = ({ filters, onChange }) => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<User[]>([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);

  useEffect(() => {
    const loadDrivers = async () => {
      try {
        setIsLoadingDrivers(true);
        const driversList = await driversApi.listDrivers();
        setDrivers(driversList);
      } catch (error) {
        console.error('Failed to load drivers:', error);
      } finally {
        setIsLoadingDrivers(false);
      }
    };

    loadDrivers();
  }, []);

  const handleChange = (key: keyof TripFiltersType, value: any) => {
    onChange({ ...filters, [key]: value });
  };

  const getCurrentMonthStart = () => {
    const now = new Date();
    return format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd');
  };

  const getCurrentMonthEnd = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Qualification Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trips.filters.statusType')}
          </label>
          <select
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.qualification || ''}
            onChange={(e) =>
              handleChange('qualification', e.target.value ? (e.target.value as any) : null)
            }
          >
            <option value="">{t('trips.filters.all')}</option>
            <option value="pending">{t('trips.filters.pending')}</option>
            <option value="professional">{t('trips.filters.professional')}</option>
            <option value="personal">{t('trips.filters.personal')}</option>
          </select>
        </div>

        {/* Driver Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trips.filters.driver')}
          </label>
          <select
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.driverId || ''}
            onChange={(e) => handleChange('driverId', e.target.value || null)}
            disabled={isLoadingDrivers}
          >
            <option value="">{t('trips.filters.allDrivers')}</option>
            {drivers.map((driver) => (
              <option key={driver._id} value={driver._id}>
                {driver.name}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trips.filters.fromDate')}
          </label>
          <input
            type="date"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.startDate || ''}
            onChange={(e) => handleChange('startDate', e.target.value || null)}
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trips.filters.toDate')}
          </label>
          <input
            type="date"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.endDate || ''}
            onChange={(e) => handleChange('endDate', e.target.value || null)}
          />
        </div>

        {/* Quick Period Buttons */}
        <div className="flex items-end">
          <button
            onClick={() => {
              handleChange('startDate', getCurrentMonthStart());
              handleChange('endDate', getCurrentMonthEnd());
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-full"
          >
            {t('trips.filters.thisMonth')}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() =>
            onChange({
              startDate: getCurrentMonthStart(),
              endDate: getCurrentMonthEnd(),
              qualification: null,
              driverId: null,
              search: '',
            })
          }
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('trips.filters.resetFilters')}
        </button>
      </div>
    </div>
  );
};
